import axios from "axios";
import type { AuthRepository } from "../../domain/repositories/AuthRepository";
import type { User } from "../../domain/entities/User";
import type { LoginDTO } from "../../domain/dto/LoginDTO";
import type { SignupDTO } from "../../domain/dto/SignupDTO";
import type { PasswordDTO } from "../../domain/dto/PasswordDTO";
import type { EnableTotp } from "../../domain/entities/EnableTotp";
import type { QrCode } from "../../domain/entities/QrCode";
import { ApiConfig } from "../config/apiConfig";
import { getApiErrorMessage, mapQrCodeResponse } from "./apiResponseUtils";
import type { RegistrationResponse } from "../../domain/entities/RegistrationResponse";
import { resolveLoginSettingsAfterLogin } from "../../application/useCases/resolveLoginSettingsAfterLogin";
import { mapLoginTokens } from "./tokenMappers";
import { normalizeOtpPreference } from "../../domain/constants/otpConstants";
import type { OtpPreference } from "../../domain/constants/otpConstants";
import { readTotpEnabledFromSources } from "../../domain/utils/readTotpEnabled";
import { getClientDeviceName } from "@/features/sessions/domain/utils/deviceName";
import { clientIpHeaders } from "../../../../core/api/clientIp";

/** Carries the HTTP status so callers can tell an expired verification token apart. */
export class ChangePasswordError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "ChangePasswordError";
    this.status = status;
  }
}

export interface RegistrationEligibilityParams {
  email?: string;
  phone?: string;
}

export interface RegistrationEligibilityResult {
  exists: boolean;
  message: string;
}

const readBooleanFlag = (
  value: unknown,
  keys: string[],
): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (!value || typeof value !== "object") {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  for (const key of keys) {
    const flag = record[key];
    if (typeof flag === "boolean") {
      return flag;
    }
  }

  return undefined;
};

const readTextEligibility = (...values: unknown[]): boolean | undefined => {
  const text = values
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toLowerCase();

  if (!text) {
    return undefined;
  }

  if (
    text.includes("does not exist") ||
    text.includes("doesn't exist") ||
    text.includes("not exist") ||
    text.includes("not registered") ||
    text.includes("no user")
  ) {
    return false;
  }

  if (
    text.includes("already") ||
    text.includes("exist") ||
    text.includes("registered") ||
    text.includes("taken")
  ) {
    return true;
  }

  if (
    text.includes("available") ||
    text.includes("eligible") ||
    text.includes("can be used")
  ) {
    return false;
  }

  return undefined;
};

const readOtpPreferenceFromLoginResponse = (
  apiData: Record<string, unknown>,
): OtpPreference | undefined => {
  const user = apiData.user as Record<string, unknown> | undefined;
  const settings =
    (apiData.settings as Record<string, unknown> | undefined) ??
    (user?.settings as Record<string, unknown> | undefined);

  return (
    normalizeOtpPreference(apiData.otp_preference) ??
    normalizeOtpPreference(user?.otp_preference) ??
    normalizeOtpPreference(settings?.otp_preference) ??
    undefined
  );
};

const readInterimLoginToken = (apiData: Record<string, unknown>): string => {
  const tokens = mapLoginTokens(apiData);
  return tokens.temp_access_token || tokens.access_token;
};

export class AuthApi implements AuthRepository {
  async login(data: LoginDTO): Promise<User> {
    try {
      const response = await axios.post(
        ApiConfig.LOGIN,
        {
          ...data,
          device_name: getClientDeviceName(),
        },
        {
          headers: await clientIpHeaders(),
        },
      );
      const envelope = response.data as Record<string, unknown>;
      const apiData = (envelope.data ?? envelope) as Record<string, unknown>;
      const tokens = mapLoginTokens({
        ...envelope,
        ...apiData,
      });

      const userRecord = apiData.user as Record<string, unknown>;

      const legacyTotpEnabled = readTotpEnabledFromSources(apiData, envelope);

      const user: User = {
        id: userRecord.id as string,
        name: userRecord.name as string,
        email: userRecord.email as string,
        phone: userRecord.phone as string,
        institute_id: userRecord.institute_id as string,
        is_active: userRecord.is_active as boolean,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        temp_access_token: tokens.temp_access_token,
        is_totp_enabled: legacyTotpEnabled,
        is_password_set: apiData.is_password_set as boolean,
        region_key:
          typeof apiData.region_key === "string" ? apiData.region_key : undefined,
        api_base_url:
          typeof apiData.api_base_url === "string"
            ? apiData.api_base_url
            : undefined,
      };

      user.otp_preference = readOtpPreferenceFromLoginResponse(apiData);

      const interimToken = readInterimLoginToken({
        ...envelope,
        ...apiData,
      });
      if (interimToken) {
        const settings = await resolveLoginSettingsAfterLogin(interimToken, {
          otpPreference: user.otp_preference,
          isTotpEnabled: legacyTotpEnabled,
        });
        user.otp_preference = settings.otpPreference ?? user.otp_preference;
        user.is_totp_enabled = settings.isTotpEnabled || legacyTotpEnabled;
      }

      return user;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Login failed. Please try again.";
      throw new Error(message);
    }
  }

  async signup(data: SignupDTO): Promise<RegistrationResponse> {
    try {
      const response = await axios.post(ApiConfig.SIGNUP, {
        email: data.email,
        name: data.name,
        phone: data.phone,
        designation_id: data.designation_id,
        institute_name: data.institute_name,
        institute_type: data.institute_type,
        institute_country_id: data.institute_country_id,
        institute_state_id: data.institute_state_id,
        institute_city_id: data.institute_city_id,
        institute_pincode: data.instutue_pincode,
        purpose: data.purpose,
        accepted_agreement_ids: data.accepted_agreement_ids,
        accepted_clause_ids: data.accepted_clause_ids,
      });

      return response.data.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Signup failed. Please try again.";

      throw new Error(errorMessage);
    }
  }

  async checkRegistrationEligibility(
    params: RegistrationEligibilityParams,
  ): Promise<RegistrationEligibilityResult> {
    try {
      const response = await axios.get(ApiConfig.REGISTRATION_EXISTS, {
        params,
      });
      const body = response.data;
      const data = body?.data;
      const dataMessage =
        data && typeof data === "object" && typeof data.message === "string"
          ? data.message
          : undefined;
      const message =
        dataMessage ||
        body?.message ||
        "This email or phone number is already registered.";

      const exists = readBooleanFlag(data, [
        "exists",
        "user_exists",
        "email_exists",
        "phone_exists",
      ]);
      const available = readBooleanFlag(data, [
        "is_available",
        "available",
        "can_register",
        "eligible",
      ]);
      const textExists = readTextEligibility(
        typeof data === "object" && data
          ? (data as { message?: string }).message
          : undefined,
        body?.message,
      );
      const alreadyRegistered = readBooleanFlag(data, [
        "hospital_admin_registered",
        "already_invited",
      ]);

      return {
        exists:
          exists ??
          (typeof available === "boolean" ? !available : undefined) ??
          alreadyRegistered ??
          textExists ??
          body?.success === false,
        message,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Unable to validate registration details. Please try again.";
      throw new Error(errorMessage);
    }
  }

  async setupPassword(
    data: PasswordDTO,
    access_token: string,
  ): Promise<EnableTotp> {
    try {
      const response = await axios.post(
        ApiConfig.SETUP_PASSWORD,
        { password: data.password },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      );

      const result = response.data.data;

      const enable = {
        user_id: result.user_id,
        email: result.email,
        message: result.message,
      };

      return enable;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Password setup failed. Please try again.";
      throw new Error(errorMessage);
    }
  }

  async resetPassword(
    data: PasswordDTO,
    temp_access_token: string,
  ): Promise<EnableTotp> {
    try {
      const response = await axios.post(
        ApiConfig.RESET_PASSWORD,
        {
          new_password: data.password,
        },
        {
          headers: {
            Authorization: `Bearer ${temp_access_token}`,
          },
        },
      );

      const result = response.data?.data ?? {};

      return {
        user_id: result.user_id ?? "",
        email: result.email ?? "",
        message: result.message ?? response.data?.message ?? "Password updated",
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const data = error.response?.data;
      const errorMessage =
        data?.message ??
        (typeof data?.detail === "string" ? data.detail : undefined) ??
        "Password reset failed. Please try again.";
      throw new Error(errorMessage);
    }
  }

  /** Requires the CHANGE_PASSWORD temp token from verify-otp / verify-totp, not the session token. */
  async changePassword(
    oldPassword: string,
    newPassword: string,
    temp_access_token: string,
  ): Promise<void> {
    try {
      await axios.post(
        ApiConfig.CHANGE_PASSWORD,
        { old_password: oldPassword, new_password: newPassword },
        {
          headers: {
            Authorization: `Bearer ${temp_access_token}`,
          },
        },
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new ChangePasswordError(
        getApiErrorMessage(error, "Failed to change password. Please try again."),
        error.response?.status,
      );
    }
  }

  async getQrCode(access_token: string): Promise<QrCode> {
    try {
      const response = await axios.get(ApiConfig.QR_CODE, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      return mapQrCodeResponse(response.data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(
        getApiErrorMessage(error, "Failed to get QR Code. Please try again."),
      );
    }
  }
}
